/* eslint-disable @typescript-eslint/no-explicit-any */
import type React from "react";
import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Upload,
  X,
  File as FileIconGeneric,
  Image as ImageIcon,
  FileText,
  Music,
  Video,
  Camera,
  FolderOpen,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { FileWithPreview } from "@/types/public";
import { convertToPersianDigits } from "./numbers";
import FileDesc from "../modules/files/file-desc";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
const isAndroid = /Android/.test(navigator.userAgent);
const isMobile = isIOS || isAndroid;
const supportsDrop = !isMobile && "draggable" in document.createElement("div");

interface FileUploadProps {
  uploadUrl: string;
  deleteUrl?: string;
  onFilesChange?: (files: FileWithPreview[]) => void;
  maxFiles?: number;
  maxSize?: number;
  acceptedTypes?: string[];
  className?: string;
  fieldName?: string;
  headers?: Record<string, string>;
  extraData?: Record<string, string | number | boolean>;
  onUploadSuccess?: (file: FileWithPreview) => void;
  onUploadError?: (file: FileWithPreview, error: any) => void;
  onDeleteSuccess?: (file: FileWithPreview) => void;
  onDeleteError?: (file: FileWithPreview, error: any) => void;
  extraCallback?: () => void;
}

const EXT_TO_MIME: Record<string, string> = {
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".heic": "image/heic",
  ".heif": "image/heif",
  ".pdf": "application/pdf",
  ".txt": "text/plain",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

const inferMimeFromName = (name?: string): string => {
  if (!name) return "";
  const dot = name.lastIndexOf(".");
  if (dot === -1) return "";
  return EXT_TO_MIME[name.slice(dot).toLowerCase()] || "";
};

const getFileIcon = (type: string) => {
  if (!type) return FileIconGeneric;
  if (type.startsWith("image/")) return ImageIcon;
  if (type.startsWith("video/")) return Video;
  if (type.startsWith("audio/")) return Music;
  if (
    type.includes("text") ||
    type.includes("document") ||
    type.includes("pdf") ||
    type.includes("msword")
  )
    return FileText;
  return FileIconGeneric;
};

const formatFileSize = (bytes: number) => {
  if (!bytes) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(k)),
    sizes.length - 1
  );
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const matchesAccepted = (file: File, acceptPatterns: string[]): boolean => {
  if (acceptPatterns.length === 0) return true;
  const type = file.type || inferMimeFromName(file.name) || "";
  const lowerName = (file.name || "").toLowerCase();

  if (lowerName.endsWith(".heic") || lowerName.endsWith(".heif")) return true;

  return acceptPatterns.some((pat) => {
    pat = pat.trim().toLowerCase();
    if (pat.startsWith(".")) return lowerName.endsWith(pat);
    if (pat.endsWith("/*")) {
      // When type is empty (common on Android content URIs), fall back to
      // name-based check for the category (e.g. "image/*" → looksLikeImage).
      if (!type) {
        const category = pat.slice(0, -2);
        if (category === "image") return looksLikeImage(lowerName);
        return false;
      }
      return type.startsWith(`${pat.slice(0, -2)}/`);
    }
    if (pat.includes("/")) return type === pat;
    return lowerName.endsWith(`.${pat}`);
  });
};

const createFileWithPreview = (
  file: File,
  onPreviewReady: (id: string, preview: string) => void
): FileWithPreview => {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const fw: FileWithPreview = {
    name: file.name,
    size: file.size,
    type: file.type,
    lastModified: file.lastModified,
    _raw: file,
    id,
    progress: 0,
    status: "idle",
    preview: undefined,
    _xhr: null,
    serverId: undefined,
    serverPayload: undefined,
  } as unknown as FileWithPreview;

  if (file.type?.startsWith("image/") || looksLikeImage(file.name)) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (dataUrl) onPreviewReady(id, dataUrl);
    };
    reader.readAsDataURL(file);
  }

  return fw;
};

const looksLikeImage = (name?: string): boolean => {
  if (!name) return false;
  return /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(name);
};

export function FileUpload({
  uploadUrl,
  deleteUrl,
  onFilesChange,
  maxFiles = 10,
  maxSize = 10,
  acceptedTypes = [
    "image/*",
    "image/heic",
    "image/heif",
    ".heic",
    ".heif",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
  ],
  className,
  fieldName = "filepond",
  headers,
  extraData,
  onUploadSuccess,
  onUploadError,
  onDeleteSuccess,
  onDeleteError,
  extraCallback,
}: FileUploadProps) {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef<FileWithPreview[]>([]);
  const { t } = useTranslation();

  const handlePreviewReady = useCallback((id: string, preview: string) => {
    setFiles((prev) => prev.map((x) => (x.id === id ? { ...x, preview } : x)));
  }, []);

  useEffect(() => {
    onFilesChange?.(files);
  }, [files, onFilesChange]);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    return () => {
      filesRef.current.forEach((f) => {
        f._xhr?.abort?.();
        if (f.preview && f.preview.startsWith("blob:")) {
          URL.revokeObjectURL(f.preview);
        }
      });
    };
  }, []);

  const uploadSingle = useCallback(
    (f: FileWithPreview): Promise<FileWithPreview> =>
      new Promise((resolve, reject) => {
        if (!uploadUrl) {
          const err = new Error("Missing uploadUrl");
          onUploadError?.(f, err);
          return reject(err);
        }
        if (!extraData || typeof (extraData as any).hash === "undefined") {
          const err = new Error("Missing required 'hash' in extraData");
          onUploadError?.(f, err);
          return reject(err);
        }

        const form = new FormData();
        const rawFile = (f as any)._raw ?? f;
        form.append(fieldName, rawFile, f.name);
        Object.entries(extraData).forEach(([k, v]) =>
          form.append(k, String(v))
        );

        const xhr = new XMLHttpRequest();
        f._xhr = xhr;
        xhr.open("POST", uploadUrl, true);
        xhr.withCredentials = true;
        if (headers) {
          Object.entries(headers).forEach(([k, v]) =>
            xhr.setRequestHeader(k, v)
          );
        }

        xhr.timeout = isMobile ? 180_000 : 60_000;

        const markError = (message: string) => {
          const err = new Error(message);
          setFiles((prev) =>
            prev.map((x) => (x.id === f.id ? { ...x, status: "error" } : x))
          );
          onUploadError?.(f, err);
          reject(err);
        };

        xhr.upload.onprogress = (evt) => {
          if (!evt.lengthComputable) return;
          const pct = Math.min(100, Math.round((evt.loaded / evt.total) * 100));
          setFiles((prev) =>
            prev.map((x) =>
              x.id === f.id ? { ...x, progress: pct, status: "uploading" } : x
            )
          );
        };

        xhr.onerror = () => markError("Network error");
        xhr.onabort = () => markError("Upload aborted");
        xhr.ontimeout = () => markError("Upload timed out");

        xhr.onreadystatechange = () => {
          if (xhr.readyState !== 4) return;
          if (xhr.status === 0) return markError("Upload failed (no response)");

          if (xhr.status >= 200 && xhr.status < 300) {
            const respText = (xhr.responseText || "").trim();
            const ct = (
              xhr.getResponseHeader("content-type") || ""
            ).toLowerCase();

            if (!ct.includes("application/json")) {
              return markError(
                respText
                  ? `Non-JSON response: ${respText.slice(0, 160)}`
                  : "Non-JSON response"
              );
            }

            let parsed: any = null;
            try {
              parsed = respText ? JSON.parse(respText) : null;
            } catch {
              return markError(respText || "Upload failed");
            }

            const serverId: string | undefined =
              parsed?.data?.id != null ? String(parsed.data.id) : undefined;

            if (serverId) {
              setFiles((prev) =>
                prev.map((x) =>
                  x.id === f.id
                    ? {
                        ...x,
                        progress: 100,
                        status: "done",
                        serverId,
                        serverPayload: parsed,
                      }
                    : x
                )
              );
              const updated = Object.assign(f, {
                progress: 100,
                status: "done",
                serverId,
                serverPayload: parsed,
              });
              onUploadSuccess?.(updated);
              resolve(updated);
            } else {
              markError("Upload failed (unexpected response)");
            }
          } else {
            markError(`Upload failed (${xhr.status})`);
          }
        };

        xhr.send(form);
      }),
    [headers, fieldName, extraData, uploadUrl, onUploadSuccess, onUploadError]
  );

  const uploadBatch = useCallback(
    async (list: FileWithPreview[]) => {
      if (list.length === 0) return;
      setIsUploading(true);
      try {
        await Promise.all(list.map(uploadSingle));
      } finally {
        setIsUploading(false);
      }
    },
    [uploadSingle]
  );

  const processFiles = useCallback(
    (fileList: FileList | File[], skipTypeCheck = false) => {
      const arr = Array.from(fileList);
      const incoming: FileWithPreview[] = [];

      for (const file of arr) {
        if (file.size > maxSize * 1024 * 1024) continue;
        // skipTypeCheck is used for the camera input: the OS already restricts
        // to images via accept="image/*", so we trust it even when file.type
        // is empty (Android content URIs with no extension).
        if (!skipTypeCheck && !matchesAccepted(file, acceptedTypes)) continue;
        incoming.push(createFileWithPreview(file, handlePreviewReady));
      }

      if (incoming.length === 0) return;

      let toUpload: FileWithPreview[] = [];
      setFiles((prev) => {
        const valid = prev.filter(
          (f) => typeof f.size === "number" && typeof f.name === "string"
        );
        const spaceLeft = Math.max(0, maxFiles - valid.length);
        toUpload = incoming.slice(0, spaceLeft);
        return [...valid, ...toUpload];
      });

      setTimeout(() => {
        if (toUpload.length > 0) uploadBatch(toUpload);
      }, 0);
    },
    [maxFiles, maxSize, acceptedTypes, uploadBatch, handlePreviewReady]
  );

  const removeFile = useCallback(
    (fileId: string) => {
      setFiles((prev) => {
        const target = prev.find((f) => f.id === fileId);
        if (!target) return prev;

        target._xhr?.abort?.();

        if (target.preview && target.preview.startsWith("blob:")) {
          URL.revokeObjectURL(target.preview);
        }

        if (deleteUrl && target.serverId) {
          (async () => {
            try {
              const url = deleteUrl.startsWith("http")
                ? new URL(deleteUrl)
                : new URL(deleteUrl, window.location.origin);
              url.searchParams.set("id", String(target.serverId));
              const res = await fetch(url.toString(), {
                method: "DELETE",
                headers,
                credentials: "include",
              });
              if (!res.ok) throw new Error(`Delete failed (${res.status})`);
              onDeleteSuccess?.(target);
            } catch (e) {
              onDeleteError?.(target, e);
            }
          })();
        }

        return prev.filter((f) => f.id !== fileId);
      });
    },
    [deleteUrl, headers, onDeleteSuccess, onDeleteError]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files?.length) processFiles(e.dataTransfer.files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) processFiles(e.target.files);
    e.target.value = "";
  };

  // Camera input already restricts to images via accept="image/*" at the OS
  // level, so skip MIME-type validation — Android content URIs often have no
  // file extension and an empty file.type, which would otherwise be rejected.
  const handleCameraSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) processFiles(e.target.files, true);
    e.target.value = "";
  };

  const isFull = files.length >= maxFiles;

  return (
    <div className={cn("w-full space-y-4", className)}>
      <Card
        className={cn(
          "border-2 border-dashed transition-colors",
          isDragOver
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25",
          isFull && "opacity-50"
        )}
        onDragOver={supportsDrop ? handleDragOver : undefined}
        onDragLeave={supportsDrop ? handleDragLeave : undefined}
        onDrop={supportsDrop ? handleDrop : undefined}
        role="region"
        aria-label={t("drop_files_here")}
      >
        <CardContent className="flex flex-col items-center justify-center lg:py-2 lg:px-6 px-10 text-center">
          <Upload
            className={cn(
              "h-12 w-12 mb-4 transition-colors",
              isDragOver ? "text-primary" : "text-muted-foreground"
            )}
          />
          <div className="space-y-1">
            <span className="text-lg font-medium hidden md:block">
              {isFull
                ? t("maximum_reached", { count: maxFiles })
                : t("drop_files_here")}
            </span>
            <p className="text-sm text-muted-foreground">
              {t("supports_per_file", {
                types: acceptedTypes.join(", "),
                maxSize,
              })}
            </p>
          </div>

          {!isFull && (
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {/* Desktop: single button */}
              {!isMobile && (
                <Button
                  variant="outline"
                  className="bg-transparent"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t("choose_files")}
                </Button>
              )}

              {/* Mobile: separate buttons for each source */}
              {isMobile && (
                <>
                  <Button
                    variant="outline"
                    className="bg-transparent gap-2"
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    <Camera className="h-4 w-4" />
                    {/* {t("camera") ?? "Camera"} */}
                  </Button>
                  <Button
                    variant="outline"
                    className="bg-transparent gap-2"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FolderOpen className="h-4 w-4" />
                    {t("choose_files")}
                  </Button>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* General file picker — Changed from "hidden" to "sr-only" for mobile compatibility */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedTypes.join(",")}
        onChange={handleFileSelect}
        className="sr-only"
      />

      {/* Camera-only input for mobile — Changed from "hidden" to "sr-only" for mobile compatibility */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraSelect}
        className="sr-only"
      />

      {files.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">
              {t("uploaded_files")} ({files.length}/{maxFiles})
            </h3>
            {isUploading && (
              <Badge variant="secondary" className="animate-pulse">
                {t("uploading")}...
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {files.map((file) => {
              const Icon = getFileIcon(
                file.type || inferMimeFromName(file.name)
              );
              const isComplete =
                (file.progress ?? 0) >= 100 && file.status === "done";
              const hasError = file.status === "error";

              return (
                <Card key={file.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      {file.preview ? (
                        <img
                          src={file.preview}
                          alt={file.name}
                          className="h-12 w-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                          <Icon className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{file.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(file.id)}
                          className="flex-shrink-0 h-8 w-8 p-0"
                          aria-label={t("remove_file")}
                          disabled={
                            file.status === "deleting" ||
                            file.status === "uploading"
                          }
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      {!isComplete && !hasError && (
                        <div className="mt-2">
                          <Progress
                            value={file.progress ?? 0}
                            className="h-2"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {convertToPersianDigits(
                              Math.round(file.progress ?? 0)
                            )}
                            % {t("uploaded")}
                          </p>
                        </div>
                      )}

                      {hasError && (
                        <Badge variant="destructive" className="mt-2 text-xs">
                          {t("upload_error")}
                        </Badge>
                      )}

                      {isComplete && (
                        <Badge variant="secondary" className="mt-2 text-xs">
                          ✓ {t("uploaded")}
                          {file.serverId ? ` (#${file.serverId})` : ""}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {file.serverId && (
                    <FileDesc
                      serviceID={file.serverId}
                      callback={() => extraCallback?.()}
                    />
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
